// OWNER: FRONTEND-ADMIN. Settings sections (SPEC §5.3, §15.9): blocked dates, calling (DNC scrub scope, main callback
// number, inbound greeting, email mailing address), internal DNC list (search/add only; no removal in the UI), audit log.
import { useState } from 'preact/hooks';
import type { Country } from '../../../src/shared/api';
import type { AuditEntry, RecycleSettings, SettingsView } from '../../../src/shared/api-core';
import { errorMessage } from '../api';
import { ErrorBanner, fmtDateTime, toast } from '../components/ui';
import { adminApi, type AdminSettings } from './endpoints';
import { formatPhone, normalizeNanp } from './phone';
import { OPS_TZ, prettyYmd, ymdInTz } from './dates';
import { Check, ConfirmModal, Field, ProblemList, inputValue, useLoad, useUnsavedWarning } from './ui';
import { ADMIN_WINDOW_BOUNDS, adminBoundsText, callingWindowProblems, greetingProblems, isYmd, mailingAddressProblems, normalizeMailingAddress } from './validate';

// ---------------------------------------------------------------- blocked dates

export function BlockedDatesAdmin(props: { settings: SettingsView; onSaved: (s: SettingsView) => void }) {
  const today = ymdInTz(new Date(), OPS_TZ);
  const [showPast, setShowPast] = useState(false);
  const [adding, setAdding] = useState<Record<Country, string>>({ US: '', CA: '' });
  const [error, setError] = useState<string | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<{ country: Country; date: string } | null>(null);

  const save = async (next: SettingsView['blockedDates']) => {
    setError(null);
    props.onSaved(await adminApi.patchSettings({ blockedDates: next }));
  };
  const add = async (country: Country) => {
    const d = adding[country];
    if (!isYmd(d)) { setError('Pick a valid date.'); return; }
    if (props.settings.blockedDates[country].includes(d)) { setError(`${prettyYmd(d)} is already blocked for ${country}.`); return; }
    try {
      await save({ ...props.settings.blockedDates, [country]: [...props.settings.blockedDates[country], d].sort() });
      setAdding({ ...adding, [country]: '' });
      toast(`${prettyYmd(d)} blocked for ${country === 'US' ? 'US' : 'Canadian'} calls.`, 'ok');
    } catch (e) { setError(errorMessage(e)); }
  };

  return (
    <div class="stack">
      <div class="adm-toolbar">
        <div class="adm-sub">No calls on these dates (prospect-local). Seeded with federal/national holidays through 2027; add provincial or state holidays you want to respect.</div>
        <span class="grow" />
        <Check checked={showPast} onChange={setShowPast} label="Show past dates" />
      </div>
      <ErrorBanner error={error} />
      <div class="adm-grid-2">
        {(['US', 'CA'] as Country[]).map((country) => {
          const dates = [...props.settings.blockedDates[country]].sort().filter((d) => showPast || d >= today);
          return (
            <div class="card stack">
              <h3>{country === 'US' ? 'United States' : 'Canada'}</h3>
              <div class="row">
                <input type="date" value={adding[country]} min={today} onInput={(e) => setAdding({ ...adding, [country]: inputValue(e) })} />
                <button class="btn btn-sm btn-ink" disabled={!adding[country]} onClick={() => void add(country)}>Block date</button>
              </div>
              {dates.length === 0 ? <div class="muted">No upcoming blocked dates.</div> : null}
              <table>
                <tbody>{dates.map((d) => (
                  <tr class={d < today ? 'adm-dim' : ''}>
                    <td class="mono">{d}</td><td>{prettyYmd(d)}</td>
                    <td style="text-align:right"><button class="btn btn-sm" onClick={() => setConfirmRemove({ country, date: d })}>Unblock</button></td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          );
        })}
      </div>
      {confirmRemove ? (
        <ConfirmModal
          title={`Unblock ${prettyYmd(confirmRemove.date)}?`}
          body={<span>Setters will be able to call {confirmRemove.country === 'US' ? 'US' : 'Canadian'} businesses on this date (within normal hours).</span>}
          confirmLabel="Unblock"
          onConfirm={async () => {
            const c = confirmRemove.country;
            await save({ ...props.settings.blockedDates, [c]: props.settings.blockedDates[c].filter((x) => x !== confirmRemove.date) });
            toast(`${prettyYmd(confirmRemove.date)} unblocked.`, 'ok');
          }}
          onClose={() => setConfirmRemove(null)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- calling

export function CallingAdmin(props: { settings: AdminSettings; onSaved: (s: AdminSettings) => void }) {
  const s = props.settings;
  const numbers = useLoad(() => adminApi.numbers(), []);
  const [scope, setScope] = useState(s.dncScrubScope);
  // Owner rules 2026-10-01 (SPEC §19). Older servers don't send these: undefined hides the control.
  const [notInterestedDnc, setNotInterestedDnc] = useState<boolean | undefined>(s.notInterestedIsDnc);
  const [recordingMode, setRecordingMode] = useState(s.recordingMode);
  const [mainId, setMainId] = useState<number | null>(s.mainNumberId);
  const [greeting, setGreeting] = useState(s.inboundGreeting);
  const addressSupported = s.mailingAddress !== undefined; // undefined: the server has no mailing_address setting yet
  const savedAddress = s.mailingAddress ?? '';
  const [address, setAddress] = useState(savedAddress);
  // SPEC §19 post-call email (migration 0020): fallback sender and BCC-to-sender. Undefined (older server) hides them.
  const [fallbackFrom, setFallbackFrom] = useState(s.salesEmailFallbackFrom ?? '');
  const [bccSender, setBccSender] = useState<boolean | undefined>(s.salesEmailBccSender);
  const [vendor, setVendor] = useState(s.transcriptionVendor);
  // Texting (2026-10-03): the carriers approved our US A2P 10DLC registration. Undefined (older server) hides it.
  const [a2p, setA2p] = useState<boolean | undefined>(s.smsUsA2pApproved);
  // PJ 2026-10-07: whether setters see the admins' activity numbers. Undefined (older server) hides it.
  const [adminNumbers, setAdminNumbers] = useState<boolean | undefined>(s.adminNumbersVisible);
  // PJ 2026-10-20: "Record (they said yes)" on calls skipped for all-party consent. Undefined (older server) hides it.
  const [consentRec, setConsentRec] = useState<boolean | undefined>(s.consentRecordingEnabled);
  // SPEC §19 (owner decision 2026-10-03): US Sunday campaign hours; null = no Sunday calls. Canada never has them.
  const savedSun = s.callingWindows.US.sunday ?? null;
  const [sunOn, setSunOn] = useState(!!savedSun);
  const [sun, setSun] = useState(savedSun ?? { start: '12:00', end: '17:00' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const addressChanged = addressSupported && normalizeMailingAddress(address) !== normalizeMailingAddress(savedAddress);
  const fallbackChanged = s.salesEmailFallbackFrom !== undefined && fallbackFrom.trim().toLowerCase() !== s.salesEmailFallbackFrom;
  const bccChanged = bccSender !== undefined && bccSender !== s.salesEmailBccSender;
  const vendorChanged = vendor !== undefined && vendor !== s.transcriptionVendor;
  const a2pChanged = a2p !== undefined && a2p !== s.smsUsA2pApproved;
  const adminNumbersChanged = adminNumbers !== undefined && adminNumbers !== s.adminNumbersVisible;
  const consentRecChanged = consentRec !== undefined && consentRec !== s.consentRecordingEnabled;
  const sundayWindow = sunOn ? sun : null;
  const sunChanged = sunOn !== !!savedSun || (sunOn && (sun.start !== savedSun?.start || sun.end !== savedSun?.end));
  const sunProblems = callingWindowProblems({ weekday: null, saturday: null, sunday: sundayWindow }, 'US', 'US calling window');
  const dirty = scope !== s.dncScrubScope || mainId !== s.mainNumberId || greeting !== s.inboundGreeting || addressChanged
    || notInterestedDnc !== s.notInterestedIsDnc || recordingMode !== s.recordingMode || fallbackChanged || bccChanged || vendorChanged || sunChanged || a2pChanged
    || adminNumbersChanged || consentRecChanged;
  useUnsavedWarning(dirty);
  const gProblems = greetingProblems(greeting);
  const aProblems = addressSupported ? mailingAddressProblems(address) : [];
  const candidates = (numbers.data ?? []).filter((n) => n.status !== 'retired' || n.id === s.mainNumberId)
    .sort((a, b) => (a.purpose === 'main' ? -1 : 0) - (b.purpose === 'main' ? -1 : 0) || a.e164.localeCompare(b.e164));
  const picked = candidates.find((n) => n.id === mainId);

  const save = async () => {
    if (gProblems.some((p) => p.level === 'error')) { setError(gProblems.find((p) => p.level === 'error')!.message); return; }
    if (addressChanged && aProblems.some((p) => p.level === 'error')) { setError(aProblems.find((p) => p.level === 'error')!.message); return; }
    if (sunChanged && sunProblems.length) { setError(sunProblems[0]); return; }
    if (fallbackChanged && !/^[a-z0-9._%+-]+@valistix\.com$/.test(fallbackFrom.trim().toLowerCase())) { setError('The fallback sender must be an @valistix.com address.'); return; }
    setBusy(true); setError(null);
    try {
      const next = await adminApi.patchSettings({
        ...(scope !== s.dncScrubScope ? { dncScrubScope: scope } : {}),
        ...(notInterestedDnc !== undefined && notInterestedDnc !== s.notInterestedIsDnc ? { notInterestedIsDnc: notInterestedDnc } : {}),
        ...(recordingMode !== undefined && recordingMode !== s.recordingMode ? { recordingMode } : {}),
        ...(mainId !== s.mainNumberId ? { mainNumberId: mainId } : {}),
        ...(greeting !== s.inboundGreeting ? { inboundGreeting: greeting } : {}),
        ...(addressChanged ? { mailingAddress: normalizeMailingAddress(address) || null } : {}),
        ...(fallbackChanged ? { salesEmailFallbackFrom: fallbackFrom.trim().toLowerCase() } : {}),
        ...(bccChanged ? { salesEmailBccSender: bccSender } : {}),
        ...(vendorChanged ? { transcriptionVendor: vendor } : {}),
        ...(a2pChanged ? { smsUsA2pApproved: a2p } : {}),
        ...(adminNumbersChanged ? { adminNumbersVisible: adminNumbers } : {}),
        ...(consentRecChanged ? { consentRecordingEnabled: consentRec } : {}),
        ...(sunChanged ? { callingWindows: { US: { ...s.callingWindows.US, sunday: sundayWindow }, CA: { ...s.callingWindows.CA, sunday: null } } } : {}),
      });
      props.onSaved(next);
      setAddress(next.mailingAddress ?? '');
      toast('Settings saved.', 'ok');
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  const win = (c: Country) => {
    const w = s.callingWindows[c];
    const p = (x: { start: string; end: string } | null) => (x ? `${x.start}–${x.end}` : 'closed');
    return `Mon–Fri ${p(w.weekday)} · Sat ${p(w.saturday)} · Sun ${p(w.sunday ?? null)}`;
  };

  return (
    <div class="adm-grid-2" style="align-items:start">
      <div class="stack">
        <div class="card stack">
          <h3>Main callback number</h3>
          <div class="adm-sub">The number every voicemail script and drop gives out as {'{callback}'}. Callbacks to it ring the team and fall through to the voicemail box below.</div>
          <ErrorBanner error={numbers.error} />
          <select value={mainId === null ? '' : String(mainId)} onChange={(e) => { const v = inputValue(e); setMainId(v ? Number(v) : null); }}>
            <option value="">Not set (setters can't record drops until it is)</option>
            {candidates.map((n) => <option value={String(n.id)}>{formatPhone(n.e164)} · {n.purpose}{n.metro ? ` · ${n.metro}` : ''} · {n.status}</option>)}
          </select>
          {picked && picked.purpose !== 'main' ? <div class="chip chip-warn" style="display:block;padding:6px 10px">This is an outbound caller-ID number. A dedicated number with purpose “main” is better: it never dials out, so its reputation stays clean.</div> : null}
          {mainId !== s.mainNumberId && s.mainNumberId !== null ? <div class="adm-sub">Existing drop recordings still say the old number; setters should re-record them.</div> : null}
        </div>
        <div class="card stack">
          <h3>National DNC scrub scope (US)</h3>
          <label class="adm-check"><input type="radio" name="scope" checked={scope === 'mobile_unknown'} onChange={() => setScope('mobile_unknown')} />
            <span><b>Mobile, VoIP and unknown lines</b> <span class="muted">(default)</span>: US numbers that may be personal lines must be scrubbed against the FTC list every 31 days.</span></label>
          <label class="adm-check"><input type="radio" name="scope" checked={scope === 'all_us'} onChange={() => setScope('all_us')} />
            <span><b>Every US number</b>: stricter; landlines need a scrub too before they can be dialled.</span></label>
          <label class="adm-check"><input type="radio" name="scope" checked={scope === 'off'} onChange={() => setScope('off')} />
            <span><b>Off (owner decision: national registry not used)</b>: no FTC scrub is needed for any line type, cell phones included, and the state "scrub every line" rules are ignored. Our own do-not-call list still blocks every number on it.</span></label>
          {scope === 'off' && s.dncScrubScope !== 'off' ? <div class="chip chip-warn" style="display:block;padding:6px 10px">Numbers already found on the national registry stay blocked; nothing else is checked against it.</div> : null}
        </div>
        {notInterestedDnc !== undefined ? (
          <div class="card stack">
            <h3>"No" means never again</h3>
            <Check checked={notInterestedDnc} onChange={setNotInterestedDnc}
              label={'"DM, not interested" adds the business to our do-not-call list for good, like a do-not-call request (owner rule 2026-10-01)'} />
            <div class="adm-sub">Every number of that business is blocked on every campaign, by anyone. Turning this off only affects future calls; numbers already added stay on the list.</div>
          </div>
        ) : null}
        {s.recycle ? <RecycleCard settings={s} onSaved={props.onSaved} /> : null}
        {recordingMode !== undefined ? (
          <div class="card stack">
            <h3>Call recording</h3>
            <div class="adm-sub">Connected calls are recorded (both sides, separate channels) and filed privately. Only booked calls (the call a meeting was booked on) can be played, by the team, on the Booked calls and Meetings pages; no page or link serves any other recording.</div>
            <label class="adm-check"><input type="radio" name="recmode" checked={recordingMode === 'one_party_only'} onChange={() => setRecordingMode('one_party_only')} />
              <span><b>Skip all-party-consent states</b> <span class="muted">(default)</span>: calls with businesses in states that need everyone's consent to record (California, Florida, Illinois, Pennsylvania, Washington and others), or with a California or Washington area code, are not recorded. Canada and every other state are.</span></label>
            <label class="adm-check"><input type="radio" name="recmode" checked={recordingMode === 'all'} onChange={() => setRecordingMode('all')} />
              <span><b>Record every connected call</b>: includes all-party-consent states without a spoken notice. California alone allows $5,000 per call in damages (Penal Code 632, 637.2). Check with a lawyer first.</span></label>
            {consentRec !== undefined ? (
              <>
                <Check checked={consentRec} onChange={setConsentRec} label={<b>Record with their yes on calls that need everyone's consent</b>} />
                <div class="adm-sub">On: on a connected call that isn't recorded because of their state (or a California or Washington area code), the setter asks, and only after their yes presses "Record (they said yes)" to record the rest of the call. Off (default): those calls stay unrecorded.</div>
              </>
            ) : null}
          </div>
        ) : null}
        {a2p !== undefined ? (
          <div class="card stack">
            <h3>US texting approval</h3>
            <Check checked={a2p} onChange={setA2p}
              label="The carriers approved our US texting registration (A2P 10DLC brand and campaign show Approved in Twilio)" />
            <div class="adm-sub">Until this is on, the text composer warns that US texts won't deliver yet, and a sent text says "queued" instead of "sent". It changes no rule: texts are still sent and Twilio reports each one's fate.</div>
          </div>
        ) : null}
        {adminNumbers !== undefined ? (
          <div class="card stack">
            <h3>Team view of admin numbers</h3>
            <Check checked={adminNumbers} onChange={setAdminNumbers} label="Show my numbers to the team" />
            <div class="adm-sub">Off: setters don't see admins' dials, connects, meetings or pace, and no team total includes them. On: they see them like anyone else's.</div>
          </div>
        ) : null}
        <div class="card stack">
          <h3>Legal calling hours (defaults)</h3>
          <dl class="adm-kv"><dt>US</dt><dd>{win('US')}</dd><dt>Canada</dt><dd>{win('CA')}</dd><dt>Legal name</dt><dd>{s.legalName}</dd></dl>
          <div class="adm-sub">Prospect-local time; a call must start at least 2 minutes before closing. Per-state overrides are in State rules. Weekday and Saturday defaults are changed through the settings API on purpose.</div>
          <div class="stack">
            <Check checked={sunOn} onChange={setSunOn} label={<b>US Sunday calls</b>} />
            {sunOn ? (
              <div class="row">
                <input type="time" value={sun.start} onInput={(e) => setSun({ ...sun, start: inputValue(e) })} /> – <input type="time" value={sun.end} onInput={(e) => setSun({ ...sun, end: inputValue(e) })} />
              </div>
            ) : <div class="adm-sub">Off: no campaign calls on Sundays.</div>}
            {sunOn && sunProblems.length ? <div class="chip chip-bad" style="display:block;padding:6px 10px">{sunProblems[0]}</div> : null}
            <div class="adm-sub">
              US only, between {ADMIN_WINDOW_BOUNDS.US.sunday?.[0]} and {ADMIN_WINDOW_BOUNDS.US.sunday?.[1]} their time. States whose law restricts Sunday
              telemarketing (marked “no Sunday calls” in State rules) and states with their own hours stay closed on Sundays. Holidays still block.
            </div>
            <div class="adm-sub"><b>Canada:</b> Sunday calls aren't allowed.</div>
          </div>
          <div class="adm-sub">
            Any window an admin sets keeps a margin inside the legal hours for time-zone guesses: US {adminBoundsText('US')}; Canada {adminBoundsText('CA')}.
            A stored window outside that is pulled in when calls are checked.
          </div>
        </div>
      </div>
      <div class="stack">
      <div class="card stack">
        <h3>Inbound voicemail greeting</h3>
        <div class="adm-sub">Read by the phone system when nobody picks up a callback. It must name {s.legalName} and offer “press 9” to stop all calls (that key adds the caller to the do-not-call list).</div>
        <textarea rows={6} value={greeting} onInput={(e) => setGreeting(inputValue(e))} />
        <div class="adm-sub">{greeting.length} characters</div>
        <ProblemList problems={gProblems} />
      </div>
      <div class="card stack">
        <h3>Mailing address for emails</h3>
        <div class="adm-sub">
          Every follow-up email ends with it ({'{mailing_address}'} in the email templates): CASL and CAN-SPAM require a valid postal address in
          every commercial email. Use a PO box or virtual-office address, not a home address. Setters' email buttons stay off until it is set.
        </div>
        {addressSupported ? (
          <>
            <textarea rows={3} value={address} placeholder={'Street or PO box\nCity, province/state, postal/ZIP code'} onInput={(e) => setAddress(inputValue(e))} maxLength={400} />
            <ProblemList problems={aProblems} />
          </>
        ) : (
          <div class="adm-muted-box">This server doesn't have the mailing-address setting yet (CORE adds <code>mailing_address</code>). Until it does, setters can't send emails.</div>
        )}
        {s.salesEmailFallbackFrom !== undefined ? (
          <>
            <label class="stack" style="gap:4px">
              <span>Sender for people without their own address</span>
              <input type="email" value={fallbackFrom} onInput={(e) => setFallbackFrom(inputValue(e))} placeholder="hello@valistix.com" />
              <span class="adm-sub">Post-call emails go out as "First name from Valistix" from each person's own @valistix.com address (Settings → Team). Anyone without one sends from this address.</span>
            </label>
            {bccSender !== undefined ? (
              <label class="row" style="gap:6px"><input type="checkbox" checked={bccSender} onChange={(e) => setBccSender((e.currentTarget as HTMLInputElement).checked)} />
                <span>BCC each email to the sender's own address (the original sits next to the prospect's reply)</span></label>
            ) : null}
            {vendor !== undefined ? (
              <label class="stack" style="gap:4px">
                <span>Call transcription for email drafts</span>
                <select value={vendor} onChange={(e) => setVendor((e.currentTarget as HTMLSelectElement).value === 'gemini' ? 'gemini' : 'cartesia')}>
                  <option value="cartesia">Cartesia first (plan credits), Gemini if it fails</option>
                  <option value="gemini">Gemini first, Cartesia if it fails</option>
                </select>
                <span class="adm-sub">Only runs when a setter drafts an email on a recorded call. The transcript stays private.</span>
              </label>
            ) : null}
          </>
        ) : null}
      </div>
      </div>
      <div style="grid-column:1/-1" class="stack">
        <ErrorBanner error={error} />
        <div class="row">
          <button class="btn btn-primary" disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
          <button class="btn" disabled={!dirty || busy} onClick={() => { setScope(s.dncScrubScope); setMainId(s.mainNumberId); setGreeting(s.inboundGreeting); setAddress(savedAddress); setNotInterestedDnc(s.notInterestedIsDnc); setRecordingMode(s.recordingMode); setSunOn(!!savedSun); setSun(savedSun ?? { start: '12:00', end: '17:00' }); setAdminNumbers(s.adminNumbersVisible); setConsentRec(s.consentRecordingEnabled); }}>Revert</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- lead recycling (SPEC §19, migration 0018)

function RecycleCard(props: { settings: AdminSettings; onSaved: (s: AdminSettings) => void }) {
  const saved = props.settings.recycle!;
  const [r, setR] = useState<RecycleSettings>(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty = r.enabled !== saved.enabled || r.exhaustedDays !== saved.exhaustedDays
    || r.extraAttempts !== saved.extraAttempts || r.hasOneDays !== saved.hasOneDays;
  useUnsavedWarning(dirty);
  const num = (k: 'exhaustedDays' | 'extraAttempts' | 'hasOneDays') => (e: Event) => setR({ ...r, [k]: Math.round(Number(inputValue(e)) || 0) });
  const save = async () => {
    setBusy(true); setError(null);
    try {
      const next = await adminApi.patchSettings({ recycle: r });
      props.onSaved(next);
      if (next.recycle) setR(next.recycle);
      toast('Recycling settings saved.', 'ok');
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div class="card stack">
      <h3>Lead recycling</h3>
      <Check checked={r.enabled} onChange={(v) => setR({ ...r, enabled: v })} label="Bring leads back automatically (checked daily)" />
      <div class="adm-sub">
        Out of attempts without reaching a decision-maker: the lead rests, then comes back once for a few more attempts at times of day it
        was never called. "Has one already": back for a fresh round after a longer rest. Never brought back: do-not-call, wrong or bad
        numbers, not a fit, and leads with a meeting. Every call still goes through the normal calling rules and the 90-day "not interested" rest.
      </div>
      <div class="row" style="gap:12px;flex-wrap:wrap">
        <Field label="Rest after out of attempts (days)" hint="7–365, default 30"><input type="number" min={7} max={365} value={r.exhaustedDays} onInput={num('exhaustedDays')} style="width:110px" /></Field>
        <Field label="Extra attempts (one round)" hint="1–6, default 3"><input type="number" min={1} max={6} value={r.extraAttempts} onInput={num('extraAttempts')} style="width:110px" /></Field>
        <Field label='Rest after "has one already" (days)' hint="30–730, default 180"><input type="number" min={30} max={730} value={r.hasOneDays} onInput={num('hasOneDays')} style="width:110px" /></Field>
      </div>
      <ErrorBanner error={error} />
      <div class="row">
        <button class="btn btn-primary" disabled={!dirty || busy} onClick={save}>{busy ? 'Saving…' : 'Save recycling'}</button>
        <button class="btn" disabled={!dirty || busy} onClick={() => setR(saved)}>Revert</button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- internal DNC

const DNC_SOURCE: Record<string, string> = { call_request: 'asked on a call', inbound_optout: 'pressed 9 on callback', manual: 'added by admin', import: 'import' };
const DNC_PAGE = 50;

export function DncAdmin() {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const list = useLoad(() => adminApi.dnc({ q: query || undefined, limit: DNC_PAGE, offset }), [query, offset]);
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const parsed = normalizeNanp(phone);

  const search = (e: Event) => {
    e.preventDefault();
    const digits = q.replace(/\D/g, '');
    // a typed phone number is searched as its E.164 digits; anything else as text
    setQuery(digits.length >= 7 && !/[a-z]/i.test(q) ? (normalizeNanp(q)?.e164 ?? digits) : q.trim());
    setOffset(0);
  };

  return (
    <div class="stack">
      <div class="card stack">
        <h3>Add a number to the do-not-call list</h3>
        <div class="adm-sub">It will never be dialled again from any campaign, and every lead with this number is closed as DNC. Numbers can't be removed in the app.</div>
        <div class="row" style="align-items:flex-end;flex-wrap:wrap">
          <Field label="Phone" hint={phone ? (parsed ? parsed.e164 : 'Not a valid US/Canadian number') : ''}><input value={phone} onInput={(e) => setPhone(inputValue(e))} placeholder="(416) 555-0123" /></Field>
          <Field label="Reason" style="flex:1;min-width:260px"><input value={reason} onInput={(e) => setReason(inputValue(e))} placeholder="e.g. Owner emailed asking us to stop calling" /></Field>
          <button class="btn btn-danger" disabled={!parsed || !reason.trim()} onClick={() => { setError(null); setConfirming(true); }}>Add to DNC…</button>
        </div>
        <ErrorBanner error={error} />
      </div>

      <div class="card stack">
        <form class="adm-toolbar" onSubmit={search}>
          <h3 style="margin:0">Internal DNC list</h3>
          <span class="grow" />
          <input placeholder="Phone, business or reason" value={q} onInput={(e) => setQ(inputValue(e))} />
          <button class="btn btn-sm" type="submit">Search</button>
          {query ? <button class="btn btn-sm" type="button" onClick={() => { setQ(''); setQuery(''); setOffset(0); }}>Clear</button> : null}
        </form>
        <ErrorBanner error={list.error} />
        {list.data && list.data.items.length === 0 ? <div class="muted">{query ? 'No match: this number is not on the internal list.' : 'The list is empty.'}</div> : null}
        {list.data && list.data.items.length ? (
          <div class="adm-table-wrap" style="max-height:55vh">
            <table>
              <thead><tr><th>Phone</th><th>Business</th><th>Reason</th><th>How</th><th>By</th><th>Added</th></tr></thead>
              <tbody>{list.data.items.map((d) => (
                <tr><td class="mono">{formatPhone(d.phoneE164)}</td><td>{d.businessName ?? '—'}</td><td>{d.reason ?? '—'}</td>
                  <td>{DNC_SOURCE[d.source] ?? d.source}</td><td>{d.createdByName ?? '—'}</td><td>{fmtDateTime(d.createdAt)}</td></tr>
              ))}</tbody>
            </table>
          </div>
        ) : null}
        {list.data ? (
          <div class="row" style="justify-content:space-between">
            <span class="adm-sub">{list.data.total === 0 ? 0 : offset + 1}–{offset + list.data.items.length} of {list.data.total.toLocaleString()}</span>
            <div class="row">
              <button class="btn btn-sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - DNC_PAGE))}>← Previous</button>
              <button class="btn btn-sm" disabled={offset + list.data.items.length >= list.data.total} onClick={() => setOffset(offset + DNC_PAGE)}>Next →</button>
            </div>
          </div>
        ) : null}
      </div>

      {confirming && parsed ? (
        <ConfirmModal
          title={`Add ${parsed.display} to do-not-call?`}
          body={<span>This is permanent from the app: <b>{parsed.display}</b> will never be dialled again, from any campaign. Reason: “{reason.trim()}”.</span>}
          confirmLabel="Add to DNC" danger
          onConfirm={async () => {
            await adminApi.addDnc(parsed.e164, reason.trim()); // a failure is shown inside the dialog
            toast(`${parsed.display} added to the do-not-call list.`, 'ok');
            setPhone(''); setReason('');
            void list.reload();
          }}
          onClose={() => setConfirming(false)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------- audit log

const AUDIT_PAGE = 100;

export function AuditAdmin() {
  const users = useLoad(() => adminApi.users(), []);
  const [action, setAction] = useState('');
  const [userId, setUserId] = useState('');
  const [entity, setEntity] = useState('');
  const [filters, setFilters] = useState<{ action?: string; userId?: number; entity?: string }>({});
  const [older, setOlder] = useState<AuditEntry[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<string | null>(null);
  const [exhausted, setExhausted] = useState(false);
  const first = useLoad(() => adminApi.audit({ ...filters, limit: AUDIT_PAGE }), [JSON.stringify(filters)]);
  const rows = [...(first.data ?? []), ...older];

  const apply = (e: Event) => {
    e.preventDefault();
    setOlder([]); setExhausted(false); setMoreError(null);
    setFilters({ action: action.trim().replace(/\.+$/, '') || undefined, userId: userId ? Number(userId) : undefined, entity: entity.trim() || undefined });
  };
  const loadMore = async () => {
    const last = rows[rows.length - 1];
    if (!last) return;
    setLoadingMore(true); setMoreError(null);
    try {
      const next = await adminApi.audit({ ...filters, before: last.id, limit: AUDIT_PAGE });
      setOlder((o) => [...o, ...next]);
      if (next.length < AUDIT_PAGE) setExhausted(true);
    } catch (e) { setMoreError(errorMessage(e)); } finally { setLoadingMore(false); }
  };

  return (
    <div class="stack">
      <form class="adm-toolbar" onSubmit={apply}>
        <Field label="Action (or its prefix)"><input value={action} placeholder="dnc, number, vm, call.prepare_blocked" onInput={(e) => setAction(inputValue(e))} /></Field>
        <Field label="User">
          <select value={userId} onChange={(e) => setUserId(inputValue(e))}>
            <option value="">Anyone</option>
            {(users.data ?? []).map((u) => <option value={String(u.id)}>{u.name}</option>)}
          </select>
        </Field>
        <Field label="Entity"><input value={entity} placeholder="business, number, campaign…" onInput={(e) => setEntity(inputValue(e))} /></Field>
        <button class="btn btn-sm btn-ink" type="submit" style="align-self:flex-end">Filter</button>
      </form>
      <ErrorBanner error={first.error} />
      {first.data && rows.length === 0 ? <div class="muted">Nothing logged for these filters.</div> : null}
      {rows.length ? (
        <div class="adm-table-wrap" style="max-height:65vh">
          <table>
            <thead><tr><th>When</th><th>Who</th><th>Action</th><th>Entity</th><th>Detail</th></tr></thead>
            <tbody>{rows.map((a) => (
              <tr>
                <td style="white-space:nowrap">{fmtDateTime(a.createdAt)}</td>
                <td>{a.userName ?? (a.userId ? `#${a.userId}` : 'system')}</td>
                <td class="mono">{a.action}</td>
                <td class="mono">{a.entity ? `${a.entity}${a.entityId ? ` #${a.entityId}` : ''}` : '—'}</td>
                <td style="max-width:520px">{a.detail === null || a.detail === undefined ? '' : (
                  <details><summary class="adm-sub" style="cursor:pointer">{summarize(a.detail)}</summary><pre class="adm-pre">{JSON.stringify(a.detail, null, 2)}</pre></details>
                )}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      ) : null}
      <ErrorBanner error={moreError} />
      {first.data && first.data.length === AUDIT_PAGE && !exhausted ? (
        <div><button class="btn btn-sm" disabled={loadingMore} onClick={() => void loadMore()}>{loadingMore ? 'Loading…' : 'Load older entries'}</button></div>
      ) : null}
    </div>
  );
}

function summarize(detail: unknown): string {
  const text = typeof detail === 'string' ? detail : JSON.stringify(detail);
  return text.length > 90 ? text.slice(0, 90) + '…' : text;
}
